// src/components/ui/sidebar.tsx
import { cn } from "@/utils"
import * as React from "react"

export interface SidebarProviderProps extends React.HTMLAttributes<HTMLDivElement> {
    children: React.ReactNode
}

const SidebarProvider = ({ children, ...props }: SidebarProviderProps) => {
    const [isOpen, setIsOpen] = React.useState(false)

    return (
        <div className="flex h-screen" {...props}>
            {React.Children.map(children, child => {
                if (React.isValidElement(child) && child.type === Sidebar) {
                    return React.cloneElement(child as React.ReactElement<any>, { isOpen, setIsOpen })
                }
                return child
            })}
        </div>
    )
}

export interface SidebarProps extends React.HTMLAttributes<HTMLDivElement> {
    isOpen?: boolean
    setIsOpen?: React.Dispatch<React.SetStateAction<boolean>>
}

const Sidebar = React.forwardRef<HTMLDivElement, SidebarProps>(
    ({ className, children, isOpen, setIsOpen, ...props }, ref) => (
        <div
            ref={ref}
            className={cn(
                "relative flex h-full w-64 flex-col bg-background border-r",
                "md:translate-x-0",
                isOpen ? "translate-x-0" : "-translate-x-full md:translate-x-0",
                "transition-transform duration-300 ease-in-out",
                className
            )}
            {...props}
        >
            {children}
        </div>
    )
)
Sidebar.displayName = "Sidebar"

export interface SidebarHeaderProps extends React.HTMLAttributes<HTMLDivElement> { }

const SidebarHeader = React.forwardRef<HTMLDivElement, SidebarHeaderProps>(
    ({ className, ...props }, ref) => (
        <div
            ref={ref}
            className={cn("flex h-16 items-center border-b px-6", className)}
            {...props}
        />
    )
)
SidebarHeader.displayName = "SidebarHeader"

export interface SidebarContentProps extends React.HTMLAttributes<HTMLDivElement> { }

const SidebarContent = React.forwardRef<HTMLDivElement, SidebarContentProps>(
    ({ className, ...props }, ref) => (
        <div
            ref={ref}
            className={cn("flex-1 overflow-auto py-4", className)}
            {...props}
        />
    )
)
SidebarContent.displayName = "SidebarContent"

export interface SidebarFooterProps extends React.HTMLAttributes<HTMLDivElement> { }

const SidebarFooter = React.forwardRef<HTMLDivElement, SidebarFooterProps>(
    ({ className, ...props }, ref) => (
        <div
            ref={ref}
            className={cn("border-t p-4", className)}
            {...props}
        />
    )
)
SidebarFooter.displayName = "SidebarFooter"

export interface SidebarGroupProps extends React.HTMLAttributes<HTMLDivElement> { }

const SidebarGroup = React.forwardRef<HTMLDivElement, SidebarGroupProps>(
    ({ className, ...props }, ref) => (
        <div
            ref={ref}
            className={cn("px-3 py-2", className)}
            {...props}
        />
    )
)
SidebarGroup.displayName = "SidebarGroup"

export interface SidebarGroupLabelProps extends React.HTMLAttributes<HTMLDivElement> { }

const SidebarGroupLabel = React.forwardRef<HTMLDivElement, SidebarGroupLabelProps>(
    ({ className, ...props }, ref) => (
        <div
            ref={ref}
            className={cn("px-3 py-2 text-xs font-medium text-muted-foreground", className)}
            {...props}
        />
    )
)
SidebarGroupLabel.displayName = "SidebarGroupLabel"

export interface SidebarGroupContentProps extends React.HTMLAttributes<HTMLDivElement> { }

const SidebarGroupContent = React.forwardRef<HTMLDivElement, SidebarGroupContentProps>(
    ({ className, ...props }, ref) => (
        <div
            ref={ref}
            className={cn("space-y-1", className)}
            {...props}
        />
    )
)
SidebarGroupContent.displayName = "SidebarGroupContent"

export interface SidebarMenuProps extends React.HTMLAttributes<HTMLElement> { }

const SidebarMenu = React.forwardRef<HTMLElement, SidebarMenuProps>(
    ({ className, ...props }, ref) => (
        <nav
            ref={ref}
            className={cn("space-y-1", className)}
            {...props}
        />
    )
)
SidebarMenu.displayName = "SidebarMenu"

export interface SidebarMenuItemProps extends React.HTMLAttributes<HTMLDivElement> { }

const SidebarMenuItem = React.forwardRef<HTMLDivElement, SidebarMenuItemProps>(
    ({ className, ...props }, ref) => (
        <div
            ref={ref}
            className={cn("", className)}
            {...props}
        />
    )
)
SidebarMenuItem.displayName = "SidebarMenuItem"

export interface SidebarMenuButtonProps extends React.HTMLAttributes<HTMLButtonElement | HTMLDivElement> {
    asChild?: boolean
}

const SidebarMenuButton = React.forwardRef<HTMLButtonElement | HTMLDivElement, SidebarMenuButtonProps>(
    ({ className, asChild = false, ...props }, ref) => {
        const Comp = asChild ? "div" : "button"

        return (
            <Comp
                ref={ref as any}
                className={cn(
                    "flex w-full items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground focus:outline-none",
                    className
                )}
                {...props}
            />
        )
    }
)
SidebarMenuButton.displayName = "SidebarMenuButton"

export interface SidebarTriggerProps extends React.ButtonHTMLAttributes<HTMLButtonElement> { }

const SidebarTrigger = React.forwardRef<HTMLButtonElement, SidebarTriggerProps>(
    ({ className, ...props }, ref) => (
        <button
            ref={ref}
            className={cn(
                "inline-flex h-8 w-8 items-center justify-center rounded-md border border-input bg-background text-sm font-medium ring-offset-background transition-colors hover:bg-accent hover:text-accent-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
                className
            )}
            {...props}
        >
            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            </svg>
            <span className="sr-only">Toggle sidebar</span>
        </button>
    )
)
SidebarTrigger.displayName = "SidebarTrigger"

export {
    Sidebar, SidebarContent,
    SidebarFooter,
    SidebarGroup, SidebarGroupContent, SidebarGroupLabel, SidebarHeader, SidebarMenu, SidebarMenuButton, SidebarMenuItem, SidebarProvider, SidebarTrigger
}

